// Piggington's Arcade — Pig Mountain (LB-256/LB-386): the canvas renderer. No React and no game
// rules: it reads a run built by lib/pigMountain.js and paints one frame of it.
//
// HOW THE SLOPE IS DRAWN. The mountain is a list of 5 m segments, each with a height, a
// sideways bend and a half-width. Every frame, each segment's near and far edges are projected
// from a camera sitting behind and above the sled; nearer segments are painted first and a
// running "highest point painted so far" hides whatever lies behind a crest. Flat cut-outs
// (trees, rocks, gates, the Crow) are then stood on their segments from far to near, scaled by
// distance. It is the technique the old arcade racing games used: convincing, cheap, and not
// true 3D — the camera cannot swing round.
//
// THE ART. Each thing is painted from Claude Design's sprite when it is loaded (artSprite,
// artBackdrop, drawCrow: pieces by id through the `art` getter, pigMountainArt.js) and from the
// shape drawn in code below when it is not. The code shapes are the fallback, not dead code:
// the game never waits on art. Sizes in metres on each sprite object are what the art is
// scaled to, so what is seen is what the collision uses.
//
// THE RIDER IS NOT DRAWN HERE. Its tracks and shadow are; the sled and the child's character
// are a DOM layer PigMountain.jsx positions over the canvas from the numbers draw() hands back
// (the sled is painted here only in the no-art fallback).
//
// Ported from the playable test the design was agreed on (1–2 Oct 2026), where the function
// bodies below were tuned by eye over several rounds. Kept in that shape deliberately.

import { PM, ZONES, segAt, groundY } from "./pigMountain.js";

const CAM_DIST = 12;      // the camera sits this far behind the sled
// Alan, 2 Oct (first play on staging): the character was too big and too high, worst on a
// phone. The camera went up from 8.3, which puts the sled level with the Throw and Boost
// buttons and shows more of the course ahead, and the rider came down from 0.68.
const CAM_H = 9.7;        // and this far above it
export const RIDER_SCALE = 0.58;

// One frame. `view` is { W, H } in CSS pixels (the caller has already applied the device
// pixel ratio to the context). `tMs` drives the idle animation (waving, bobbing, flapping).
// `art` is pigMountainArt.js's pmPiece (id -> { c, w, h } or null), or nothing: every piece
// falls back to its placeholder shape when the art is not there, so the game never waits on it.
// Returns where the rider is on screen: { px, py, u, rot, giant, crow }.
export function drawScene(ctx, S, view, tMs, art) {
  var W = view.W, H = view.H, F = H * 0.95, HY = H * 0.14, lastT = tMs;
  var out = { px: W / 2, py: H * 0.8, u: (F / CAM_DIST) * RIDER_SCALE, rot: 0, giant: false, crow: null };
  var curZone = S.zone;   // the zone of the segment being painted, for zone-dressed scenery
  // Paint one art piece standing on (sx, sy). fit 'h' scales it to the thing's height, 'w' to its
  // width, anything else leaves it at the size it was drawn. Returns false when there is no art.
  function piece(id, sx, sy, sc, o, fit, flip) {
    var p = art ? art(id) : null;
    if (!p) return false;
    var k = fit === 'h' ? o.h / p.h : fit === 'w' ? o.w / p.w : 1, w = p.w * k * sc, h = p.h * k * sc;
    if (flip) { ctx.save(); ctx.translate(sx, 0); ctx.scale(-1, 1); ctx.drawImage(p.c, -w / 2, sy - h, w, h); ctx.restore(); }
    else ctx.drawImage(p.c, sx - w / 2, sy - h, w, h);
    return true;
  }
  // The art for one track thing. True when it was painted (or is deliberately not shown).
  function artSprite(o, sx, sy, sc) {
    var t = o.t, Z = ZONES[curZone], v = o.v | 0, fr = (lastT / 180) & 1, id;
    switch (t) {
      case 'tree': return piece(Z.fog ? (o.h > 4.5 ? 'dd-pine-snowy-2' : 'dd-pine-snowy-1') : o.h > 4.8 ? 'dd-pine-3' : o.h > 4.1 ? 'dd-pine-2' : 'dd-pine-1', sx, sy, sc, o, 'h');
      case 'pine': return piece('dd-pine-small', sx, sy, sc, o, 'h');
      case 'lodge': return piece(Z.night ? 'dd-lodge-night' : 'dd-lodge-' + (1 + v % 3), sx, sy, sc, o, 'h');
      case 'person': return piece(Z.ice ? 'dd-skater-' + (1 + v % 2) : 'dd-spectator-' + (1 + v % 5), sx, sy, sc, o, 'h');
      case 'snowman': return piece('dd-snowman', sx, sy, sc, o, 'h');
      case 'sign': return piece(Z.fog ? 'dd-piste-sign-buried' : v ? 'dd-piste-sign-1' : 'dd-piste-sign-2', sx, sy, sc, o, 'h');
      case 'pillar': return piece(o.h > 7.6 ? 'dd-ice-pillar-3' : o.h > 6.2 ? 'dd-ice-pillar-2' : 'dd-ice-pillar-1', sx, sy, sc, o, 'h');
      case 'lantern': return piece('dd-lantern-post', sx, sy, sc, o, 'h');
      case 'pole': return piece('dd-edge-pole', sx, sy, sc, o, 'h');
      case 'flag': return piece(o.mine ? 'dd-best-flag-marigold' : 'dd-best-flag-blue', sx, sy, sc, o, 'h');
      case 'lip': {         // tiles sideways across the whole course
        var lp = art ? art('dd-crevasse-lip') : null;
        if (!lp) return false;
        var lh = o.h * sc, lw = lp.w * (o.h / lp.h) * sc, x0 = sx - (o.w * sc) / 2, x1 = sx + (o.w * sc) / 2, lx;
        ctx.save(); ctx.beginPath(); ctx.rect(x0, sy - lh - 2, x1 - x0, lh + 4); ctx.clip();
        for (lx = x0; lx < x1; lx += lw - 1) ctx.drawImage(lp.c, lx, sy - lh, lw, lh);
        ctx.restore();
        return true;
      }
      case 'rock': return piece('dd-rock-' + (1 + (Math.abs(Math.round(o.x * 3)) & 1)), sx, sy, sc, o, 'w');
      case 'log': return piece('dd-log', sx, sy, sc, o, 'w');
      // 'ramp' is not here on purpose: the art's kicker is side-on, and from behind the sled it read
      // as pointing sideways (Alan, 2 Oct). The code drawing below shows it from behind.
      case 'mogul': return piece('dd-mogul', sx, sy, sc, o, 'w');
      case 'welly': return piece('dd-welly', sx, sy, sc, o, 'w');
      case 'anvil': return piece('dd-anvil', sx, sy, sc, o, 'w');
      case 'skier': return piece('dd-skier-' + (1 + fr) + ['', '-b', '-c'][(o.c | 0) % 3], sx, sy, sc, o, '', o.dir < 0);
      case 'penguin': return piece('dd-penguin-slide', sx, sy, sc, o, '', o.dir < 0);
      case 'boulder': return piece('dd-snowball-giant-' + (1 + fr), sx, sy, sc, o, 'w');
      case 'gate': return piece(o.alt ? 'dd-gate-red' : 'dd-gate-marigold', sx, sy, sc, o, 'w');
      case 'start': return piece('dd-start-banner', sx, sy, sc, o, 'w');
      case 'finish': return piece('dd-finish-banner', sx, sy, sc, o, 'w');
      case 'pad': {         // the art's chevrons point right; down the course is up the screen,
        var ap = art ? art('dd-boost-arrows') : null;   // so they are turned, and laid flat on the snow
        if (!ap) return false;
        var ak = (o.w * 0.8) / ap.h, al = ap.w * ak * sc, aw = ap.h * ak * sc, flat = 0.5;
        ctx.save(); ctx.translate(sx, sy - (al * flat) / 2); ctx.scale(1, flat); ctx.rotate(-Math.PI / 2);
        ctx.drawImage(ap.c, -al / 2, -aw / 2, al, aw); ctx.restore();
        return true;
      }
      // things to collect: gone once collected, and a marker bobs over each
      case 'can': id = 'dd-boost-can'; break;
      case 'snow': id = 'dd-snowball-stack'; break;
      case 'tinhat': id = 'dd-tin-hat'; break;
      case 'rocket': id = 'dd-rocket'; break;
      case 'giant': id = 'dd-snowball-powerup'; break;
      case 'tophat': id = 'dd-top-hat'; break;
      default: return false;
    }
    var p = art ? art(id) : null;
    if (!p) return false;
    if (o.hit) return true;
    var pk = Math.max(1, o.w / p.w), ph = p.h * pk * sc, bob = Math.max(3, sc * 0.3);
    ctx.drawImage(p.c, sx - (p.w * pk * sc) / 2, sy - ph, p.w * pk * sc, ph);
    var mk = art('dd-pickup-marker');
    if (mk) { var mw = Math.max(8, mk.w * sc); ctx.drawImage(mk.c, sx - mw / 2, sy - ph - mw * 1.25 - Math.sin(lastT / 160) * bob * 0.35, mw, mw); }
    return true;
  }

  // The far mountains for the zone, tiled sideways, and the cave's icicles along the roof.
  function artBackdrop(Z) {
    var b = art ? art('dd-backdrop-' + Z.id) : null;
    if (!b) return false;
    var bh = HY * 1.08, bw = b.w * (bh / b.h), off = ((S.x * 4) % bw + bw) % bw, bx;
    for (bx = -off; bx < W; bx += bw - 1) ctx.drawImage(b.c, bx, HY - bh + HY * 0.08, bw, bh);
    if (Z.cave) {
      var ic = art('dd-icicles');
      if (ic) { var ih = HY * 0.62, iw = ic.w * (ih / ic.h); for (bx = -off * 0.5 - iw; bx < W; bx += iw - 1) ctx.drawImage(ic.c, bx, 0, iw, ih); }
    }
    return true;
  }

  function backdrop() {
    var Z = ZONES[S.zone];
    ctx.fillStyle = Z.sky; ctx.fillRect(0, 0, W, H);
    if (artBackdrop(Z)) { ctx.fillStyle = Z.val; ctx.fillRect(0, HY + HY * 0.08, W, H - HY); return; }
    ctx.fillStyle = Z.mtn; ctx.beginPath(); ctx.moveTo(0, HY);
    var pts = [0.0, 0.55, 0.14, 0.15, 0.3, 0.7, 0.47, 0.05, 0.66, 0.6, 0.82, 0.25, 1.0, 0.65];
    for (var i = 0; i < pts.length; i += 2) ctx.lineTo(pts[i] * W, HY - (1 - pts[i + 1]) * H * 0.09);
    ctx.lineTo(W, HY); ctx.closePath(); ctx.fill();
    // the valley far below: this band is what makes the slope read as downhill
    ctx.fillStyle = Z.val; ctx.fillRect(0, HY, W, H - HY);
    var x;
    if (Z.cave) {           // a rock roof with icicles instead of sky
      ctx.fillStyle = '#0B1B2C'; ctx.fillRect(0, 0, W, HY * 0.75);
      ctx.fillStyle = '#7FB6D6';
      for (x = 0; x < W; x += 26) { var ih = HY * (0.35 + ((x * 7) % 5) * 0.1); ctx.beginPath(); ctx.moveTo(x, HY * 0.75); ctx.lineTo(x + 9, HY * 0.75 + ih); ctx.lineTo(x + 18, HY * 0.75); ctx.fill(); }
    } else if (Z.night) {   // stars and a moon
      ctx.fillStyle = '#F4F1D0';
      for (x = 11; x < W; x += 37) ctx.fillRect(x, (x * 13) % (HY * 0.8), 2, 2);
      ctx.beginPath(); ctx.arc(W * 0.8, HY * 0.35, H * 0.022, 0, 7); ctx.fill();
    } else if (!Z.fog) {
      ctx.fillStyle = '#8FB5A6';
      for (x = 6; x < W; x += 22) { ctx.beginPath(); ctx.moveTo(x, HY + H * 0.05); ctx.lineTo(x + 6, HY + H * 0.022); ctx.lineTo(x + 12, HY + H * 0.05); ctx.fill(); }
    }
  }

  function drawSprite(o, sx, sy, sc) {
    if (o.t === 'trigRaid' || o.t === 'trigBoulder') return;
    var w = o.w * sc, h = o.h * sc;
    if (w < 1.2 && o.t !== 'pole') return;
    if (art && artSprite(o, sx, sy, sc)) return;
    var t = o.t;
    if (t === 'tree' || t === 'pine') {
      ctx.fillStyle = '#7A5A3C'; ctx.fillRect(sx - w * 0.07, sy - h * 0.18, w * 0.14, h * 0.18);
      ctx.fillStyle = t === 'pine' ? '#3D8062' : '#2E6B52';
      ctx.beginPath(); ctx.moveTo(sx, sy - h); ctx.lineTo(sx - w / 2, sy - h * 0.15); ctx.lineTo(sx + w / 2, sy - h * 0.15); ctx.fill();
    } else if (t === 'lodge') {
      var wall = ['#8B5E3C', '#A0694A', '#74513A'][o.v];
      ctx.fillStyle = wall; ctx.fillRect(sx - w * 0.42, sy - h * 0.6, w * 0.84, h * 0.6);
      ctx.fillStyle = '#5E4230'; ctx.beginPath(); ctx.moveTo(sx - w / 2, sy - h * 0.55); ctx.lineTo(sx, sy - h); ctx.lineTo(sx + w / 2, sy - h * 0.55); ctx.fill();
      ctx.fillStyle = '#FFFFFF'; ctx.beginPath(); ctx.moveTo(sx - w / 2, sy - h * 0.6); ctx.lineTo(sx, sy - h * 1.06); ctx.lineTo(sx + w / 2, sy - h * 0.6); ctx.lineTo(sx + w * 0.42, sy - h * 0.6); ctx.lineTo(sx, sy - h * 0.93); ctx.lineTo(sx - w * 0.42, sy - h * 0.6); ctx.fill();
      ctx.fillStyle = '#5E4230'; ctx.fillRect(sx - w * 0.07, sy - h * 0.34, w * 0.14, h * 0.34);
      ctx.fillStyle = '#FFC629'; ctx.fillRect(sx - w * 0.32, sy - h * 0.42, w * 0.15, h * 0.17); ctx.fillRect(sx + w * 0.17, sy - h * 0.42, w * 0.15, h * 0.17);
      ctx.fillStyle = '#74513A'; ctx.fillRect(sx + w * 0.2, sy - h * 1.02, w * 0.09, h * 0.2);
    } else if (t === 'person') {
      var coat = ['#D94B4B', '#3D7CC9', '#F08A3C', '#7A55C7', '#2E9E7A'][o.v], skin = ['#F1C9A5', '#C68E63', '#8D5A3B', '#E5B48A', '#A9714B'][o.v];
      ctx.fillStyle = '#1B2A4A'; ctx.fillRect(sx - w * 0.3, sy - h * 0.42, w * 0.25, h * 0.42); ctx.fillRect(sx + w * 0.05, sy - h * 0.42, w * 0.25, h * 0.42);
      ctx.fillStyle = coat; ctx.fillRect(sx - w * 0.42, sy - h * 0.8, w * 0.84, h * 0.42);
      ctx.strokeStyle = coat; ctx.lineWidth = Math.max(1, w * 0.24); ctx.lineCap = 'round'; ctx.beginPath();
      if (o.wave) { var wv = Math.sin(lastT / 140 + o.x) * w * 0.25; ctx.moveTo(sx + w * 0.4, sy - h * 0.74); ctx.lineTo(sx + w * 0.75 + wv, sy - h * 1.05); }
      else { ctx.moveTo(sx + w * 0.45, sy - h * 0.76); ctx.lineTo(sx + w * 0.55, sy - h * 0.48); }
      ctx.moveTo(sx - w * 0.45, sy - h * 0.76); ctx.lineTo(sx - w * 0.55, sy - h * 0.48); ctx.stroke();
      ctx.fillStyle = skin; ctx.beginPath(); ctx.arc(sx, sy - h * 0.9, w * 0.3, 0, 7); ctx.fill();
      ctx.fillStyle = coat; ctx.beginPath(); ctx.arc(sx, sy - h * 0.94, w * 0.31, Math.PI, 0); ctx.fill();
    } else if (t === 'snowman') {
      ctx.fillStyle = '#FFFFFF'; ctx.strokeStyle = '#9DBBD0'; ctx.lineWidth = Math.max(1, sc * 0.05);
      ctx.beginPath(); ctx.arc(sx, sy - h * 0.3, w * 0.5, 0, 7); ctx.fill(); ctx.stroke();
      ctx.beginPath(); ctx.arc(sx, sy - h * 0.72, w * 0.34, 0, 7); ctx.fill(); ctx.stroke();
      ctx.fillStyle = '#1B2A4A'; ctx.fillRect(sx - w * 0.3, sy - h * 0.98, w * 0.6, h * 0.06); ctx.fillRect(sx - w * 0.2, sy - h * 1.14, w * 0.4, h * 0.17);
      ctx.fillStyle = '#F08A3C'; ctx.beginPath(); ctx.moveTo(sx, sy - h * 0.76); ctx.lineTo(sx + w * 0.3, sy - h * 0.7); ctx.lineTo(sx, sy - h * 0.66); ctx.fill();
    } else if (t === 'sign') {
      ctx.fillStyle = '#74513A'; ctx.fillRect(sx - w * 0.05, sy - h, w * 0.1, h);
      ctx.fillStyle = o.v ? '#3D7CC9' : '#D94B4B'; ctx.fillRect(sx - w / 2, sy - h, w, h * 0.42);
      ctx.strokeStyle = '#FFFFFF'; ctx.lineWidth = Math.max(1, sc * 0.09); ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.beginPath();
      ctx.moveTo(sx - w * 0.22, sy - h * 0.88); ctx.lineTo(sx, sy - h * 0.7); ctx.lineTo(sx + w * 0.22, sy - h * 0.88); ctx.stroke();
    } else if (t === 'pillar') {
      ctx.fillStyle = '#7FB6D6'; ctx.beginPath(); ctx.moveTo(sx - w / 2, sy); ctx.lineTo(sx - w * 0.3, sy - h); ctx.lineTo(sx + w * 0.3, sy - h); ctx.lineTo(sx + w / 2, sy); ctx.fill();
      ctx.fillStyle = '#B9DDF0'; ctx.fillRect(sx - w * 0.12, sy - h, w * 0.14, h);
    } else if (t === 'lantern') {
      ctx.fillStyle = '#0F1A30'; ctx.fillRect(sx - Math.max(1, w * 0.1), sy - h, Math.max(2, w * 0.2), h);
      ctx.fillStyle = 'rgba(255,198,41,.28)'; ctx.beginPath(); ctx.arc(sx, sy - h, w * 1.6, 0, 7); ctx.fill();
      ctx.fillStyle = '#FFC629'; ctx.beginPath(); ctx.arc(sx, sy - h, w * 0.6, 0, 7); ctx.fill();
    } else if (t === 'flag') {
      ctx.fillStyle = '#1B2A4A'; ctx.fillRect(sx - Math.max(1, sc * 0.06), sy - h, Math.max(2, sc * 0.12), h);
      ctx.fillStyle = o.mine ? '#FFC629' : '#3D7CC9'; ctx.strokeStyle = '#1B2A4A'; ctx.lineWidth = Math.max(1, sc * 0.05);
      ctx.beginPath(); ctx.moveTo(sx, sy - h); ctx.lineTo(sx + w, sy - h * 0.82); ctx.lineTo(sx, sy - h * 0.62); ctx.closePath(); ctx.fill(); ctx.stroke();
      ctx.fillStyle = '#1B2A4A'; ctx.beginPath(); ctx.arc(sx + w * 0.32, sy - h * 0.82, w * 0.1, 0, 7); ctx.fill();
    } else if (t === 'lip') {
      ctx.fillStyle = '#FFFFFF'; ctx.fillRect(sx - w / 2, sy - h, w, h);
      ctx.fillStyle = '#C9DCE8'; ctx.fillRect(sx - w / 2, sy - h * 0.25, w, h * 0.25);
      ctx.fillStyle = '#F08A3C'; ctx.fillRect(sx - w / 2, sy - h - Math.max(2, sc * 0.16), w, Math.max(2, sc * 0.16));
    } else if (t === 'tinhat') {
      if (o.hit) return;
      ctx.fillStyle = '#A9B4C0'; ctx.strokeStyle = '#1B2A4A'; ctx.lineWidth = Math.max(1, sc * 0.06);
      ctx.beginPath(); ctx.arc(sx, sy - h * 0.35, w * 0.42, Math.PI, 0); ctx.closePath(); ctx.fill(); ctx.stroke();
      ctx.fillRect(sx - w * 0.6, sy - h * 0.38, w * 1.2, h * 0.12); ctx.strokeRect(sx - w * 0.6, sy - h * 0.38, w * 1.2, h * 0.12);
      marker(sx, sy - h * 1.2, sc);
    } else if (t === 'rocket') {
      if (o.hit) return;
      ctx.fillStyle = '#D94B4B'; ctx.strokeStyle = '#1B2A4A'; ctx.lineWidth = Math.max(1, sc * 0.06);
      ctx.beginPath(); ctx.moveTo(sx, sy - h * 1.25); ctx.lineTo(sx + w * 0.3, sy - h * 0.7); ctx.lineTo(sx + w * 0.3, sy - h * 0.15); ctx.lineTo(sx - w * 0.3, sy - h * 0.15); ctx.lineTo(sx - w * 0.3, sy - h * 0.7); ctx.closePath(); ctx.fill(); ctx.stroke();
      ctx.fillStyle = '#F08A3C'; ctx.beginPath(); ctx.moveTo(sx - w * 0.2, sy - h * 0.15); ctx.lineTo(sx, sy + h * 0.15); ctx.lineTo(sx + w * 0.2, sy - h * 0.15); ctx.fill();
      marker(sx, sy - h * 1.6, sc);
    } else if (t === 'giant') {
      if (o.hit) return;
      ctx.fillStyle = '#FFFFFF'; ctx.strokeStyle = '#3D7CC9'; ctx.lineWidth = Math.max(1.5, sc * 0.1);
      ctx.beginPath(); ctx.arc(sx, sy - h * 0.6, w * 0.6, 0, 7); ctx.fill(); ctx.stroke();
      ctx.fillStyle = '#3D7CC9'; ctx.beginPath(); ctx.arc(sx, sy - h * 0.6, w * 0.2, 0, 7); ctx.fill();
      marker(sx, sy - h * 1.5, sc);
    } else if (t === 'tophat') {
      if (o.hit) return;
      ctx.fillStyle = '#0F1A30'; ctx.fillRect(sx - w * 0.3, sy - h, w * 0.6, h * 0.8); ctx.fillRect(sx - w * 0.55, sy - h * 0.22, w * 1.1, h * 0.22);
      ctx.fillStyle = '#FFC629'; ctx.fillRect(sx - w * 0.3, sy - h * 0.42, w * 0.6, h * 0.14);
      marker(sx, sy - h * 1.5, sc);
    } else if (t === 'welly') {
      ctx.fillStyle = '#2E9E7A'; ctx.fillRect(sx - w * 0.2, sy - h, w * 0.4, h); ctx.fillRect(sx - w * 0.2, sy - h * 0.35, w * 0.7, h * 0.35);
      ctx.fillStyle = '#1B6B52'; ctx.fillRect(sx - w * 0.2, sy - h, w * 0.4, h * 0.14);
    } else if (t === 'anvil') {
      ctx.fillStyle = '#5B6577'; ctx.fillRect(sx - w * 0.55, sy - h, w * 1.1, h * 0.38); ctx.fillRect(sx - w * 0.22, sy - h * 0.65, w * 0.44, h * 0.4); ctx.fillRect(sx - w * 0.42, sy - h * 0.28, w * 0.84, h * 0.28);
    } else if (t === 'skier') {
      var kc = ['#D94B4B', '#3D7CC9', '#F08A3C', '#7A55C7', '#2E9E7A'][o.c], kw = 0.7 * sc, kh = 1.7 * sc;
      ctx.fillStyle = '#1B2A4A'; ctx.fillRect(sx - kw * 1.1, sy - kh * 0.05, kw * 2.2, kh * 0.05);
      ctx.fillRect(sx - kw * 0.28, sy - kh * 0.42, kw * 0.56, kh * 0.4);
      ctx.fillStyle = kc; ctx.fillRect(sx - kw * 0.42, sy - kh * 0.8, kw * 0.84, kh * 0.42);
      ctx.fillStyle = '#F1C9A5'; ctx.beginPath(); ctx.arc(sx, sy - kh * 0.9, kw * 0.3, 0, 7); ctx.fill();
      ctx.fillStyle = kc; ctx.beginPath(); ctx.arc(sx, sy - kh * 0.94, kw * 0.31, Math.PI, 0); ctx.fill();
    } else if (t === 'penguin') {
      var pw = 0.7 * sc, ph = 0.95 * sc;
      ctx.fillStyle = '#1B2A4A'; ctx.beginPath(); ctx.ellipse(sx, sy - ph * 0.5, pw * 0.5, ph * 0.5, 0, 0, 7); ctx.fill();
      ctx.fillStyle = '#FFFFFF'; ctx.beginPath(); ctx.ellipse(sx, sy - ph * 0.42, pw * 0.3, ph * 0.36, 0, 0, 7); ctx.fill();
      ctx.fillStyle = '#F08A3C'; ctx.beginPath(); ctx.moveTo(sx - pw * 0.1, sy - ph * 0.78); ctx.lineTo(sx + pw * 0.1, sy - ph * 0.78); ctx.lineTo(sx, sy - ph * 0.66); ctx.fill();
    } else if (t === 'boulder') {
      var br2 = 1.9 * sc;
      ctx.fillStyle = '#FFFFFF'; ctx.strokeStyle = '#9DBBD0'; ctx.lineWidth = Math.max(2, sc * 0.1);
      ctx.beginPath(); ctx.arc(sx, sy - br2, br2, 0, 7); ctx.fill(); ctx.stroke();
      ctx.save(); ctx.translate(sx, sy - br2); ctx.rotate(lastT / 120); ctx.beginPath(); ctx.arc(0, 0, br2 * 0.6, 0.3, 2.2); ctx.stroke(); ctx.beginPath(); ctx.arc(0, 0, br2 * 0.3, 3.4, 5.4); ctx.stroke(); ctx.restore();
    } else if (t === 'pole') {
      ctx.fillStyle = '#F08A3C'; ctx.fillRect(sx - Math.max(1, w / 2), sy - h, Math.max(2, w), h);
    } else if (t === 'rock') {
      ctx.fillStyle = '#8A97A6'; ctx.beginPath(); ctx.moveTo(sx - w / 2, sy); ctx.lineTo(sx - w * 0.3, sy - h * 0.8); ctx.lineTo(sx + w * 0.1, sy - h); ctx.lineTo(sx + w / 2, sy - h * 0.35); ctx.lineTo(sx + w / 2, sy); ctx.fill();
      ctx.fillStyle = '#A9B4C0'; ctx.beginPath(); ctx.moveTo(sx - w * 0.3, sy - h * 0.8); ctx.lineTo(sx + w * 0.1, sy - h); ctx.lineTo(sx, sy - h * 0.5); ctx.fill();
    } else if (t === 'log') {
      ctx.fillStyle = '#8B5E3C'; ctx.fillRect(sx - w / 2, sy - h, w, h);
      ctx.fillStyle = '#C99A6B'; ctx.beginPath(); ctx.arc(sx + w / 2, sy - h / 2, h / 2, 0, 7); ctx.fill();
    } else if (t === 'ramp') {
      // a snow kicker seen from behind, in the art's style: navy outline, orange lip across the top
      var lw = Math.max(1.5, sc * 0.065);
      ctx.lineJoin = 'round'; ctx.strokeStyle = '#1B2A4A'; ctx.lineWidth = lw;
      ctx.fillStyle = '#DCE8F0'; ctx.beginPath(); ctx.moveTo(sx - w / 2, sy); ctx.lineTo(sx + w / 2, sy); ctx.lineTo(sx + w * 0.4, sy - h); ctx.lineTo(sx - w * 0.4, sy - h); ctx.closePath(); ctx.fill(); ctx.stroke();
      ctx.fillStyle = '#FFFFFF'; ctx.beginPath(); ctx.moveTo(sx - w * 0.4, sy - lw / 2); ctx.lineTo(sx + w * 0.4, sy - lw / 2); ctx.lineTo(sx + w * 0.33, sy - h + lw / 2); ctx.lineTo(sx - w * 0.33, sy - h + lw / 2); ctx.fill();
      var lipH = Math.max(3, sc * 0.2);
      ctx.fillStyle = '#F08A3C'; ctx.beginPath(); ctx.rect(sx - w * 0.4, sy - h - lipH * 0.5, w * 0.8, lipH); ctx.fill(); ctx.stroke();
      ctx.strokeStyle = '#F08A3C'; ctx.lineWidth = Math.max(1.5, sc * 0.08); ctx.lineCap = 'round'; ctx.beginPath();
      ctx.moveTo(sx - w * 0.14, sy - h * 0.2); ctx.lineTo(sx, sy - h * 0.42); ctx.lineTo(sx + w * 0.14, sy - h * 0.2);
      ctx.moveTo(sx - w * 0.14, sy - h * 0.52); ctx.lineTo(sx, sy - h * 0.74); ctx.lineTo(sx + w * 0.14, sy - h * 0.52); ctx.stroke();
    } else if (t === 'mogul') {
      ctx.fillStyle = '#FFFFFF'; ctx.beginPath(); ctx.ellipse(sx, sy, w / 2, h, 0, Math.PI, 0); ctx.fill();
      ctx.fillStyle = '#C9DCE8'; ctx.beginPath(); ctx.ellipse(sx + w * 0.12, sy, w * 0.38, h * 0.62, 0, Math.PI * 1.5, 0); ctx.lineTo(sx + w * 0.12, sy); ctx.fill();
      ctx.strokeStyle = '#9DBBD0'; ctx.lineWidth = Math.max(1, sc * 0.05); ctx.beginPath(); ctx.ellipse(sx, sy, w / 2, h, 0, Math.PI, 0); ctx.stroke();
    } else if (t === 'pad') {
      if (o.hit) return;
      ctx.strokeStyle = '#FFC629'; ctx.lineWidth = Math.max(2, sc * 0.22); ctx.lineCap = 'round'; ctx.lineJoin = 'round';
      for (var j = 0; j < 2; j++) { var yy = sy - j * sc * 0.45; ctx.beginPath(); ctx.moveTo(sx - w / 2, yy); ctx.lineTo(sx, yy - sc * 0.4); ctx.lineTo(sx + w / 2, yy); ctx.stroke(); }
    } else if (t === 'snow') {
      if (o.hit) return;
      ctx.fillStyle = '#FFFFFF'; ctx.strokeStyle = '#6F97B3'; ctx.lineWidth = Math.max(1, sc * 0.07);
      var br = w * 0.27;
      ctx.beginPath(); ctx.arc(sx - br * 0.95, sy - br, br, 0, 7); ctx.fill(); ctx.stroke();
      ctx.beginPath(); ctx.arc(sx + br * 0.95, sy - br, br, 0, 7); ctx.fill(); ctx.stroke();
      ctx.beginPath(); ctx.arc(sx, sy - br * 2.5, br, 0, 7); ctx.fill(); ctx.stroke();
      marker(sx, sy - br * 3.9, sc);
    } else if (t === 'can') {
      if (o.hit) return;
      ctx.fillStyle = '#FFC629'; ctx.strokeStyle = '#1B2A4A'; ctx.lineWidth = Math.max(1, sc * 0.08);
      ctx.beginPath(); ctx.arc(sx, sy - h * 0.6, w / 2, 0, 7); ctx.fill(); ctx.stroke();
      ctx.fillStyle = '#1B2A4A'; ctx.beginPath(); ctx.moveTo(sx + w * 0.08, sy - h * 0.95); ctx.lineTo(sx - w * 0.2, sy - h * 0.55); ctx.lineTo(sx, sy - h * 0.55); ctx.lineTo(sx - w * 0.08, sy - h * 0.25); ctx.lineTo(sx + w * 0.2, sy - h * 0.65); ctx.lineTo(sx, sy - h * 0.65); ctx.fill();
      marker(sx, sy - h * 1.35, sc);
    } else if (t === 'gate') {
      var col = o.alt ? '#D94B4B' : '#FFC629';
      for (var s = -1; s <= 1; s += 2) {
        var gx = sx + s * 1.2 * sc;
        ctx.fillStyle = '#1B2A4A'; ctx.fillRect(gx - Math.max(1, sc * 0.05), sy - h, Math.max(2, sc * 0.1), h);
        ctx.fillStyle = col; ctx.beginPath(); ctx.moveTo(gx, sy - h); ctx.lineTo(gx + sc * 0.7, sy - h * 0.8); ctx.lineTo(gx, sy - h * 0.6); ctx.fill();
      }
    } else if (t === 'start' || t === 'finish') {
      ctx.fillStyle = '#1B2A4A';
      ctx.fillRect(sx - w / 2, sy - h, Math.max(2, sc * 0.14), h); ctx.fillRect(sx + w / 2 - sc * 0.14, sy - h, Math.max(2, sc * 0.14), h);
      ctx.fillStyle = t === 'finish' ? '#FFC629' : '#FFFFFF'; ctx.fillRect(sx - w / 2, sy - h, w, h * 0.28);
      ctx.strokeStyle = '#1B2A4A'; ctx.lineWidth = Math.max(1, sc * 0.06); ctx.strokeRect(sx - w / 2, sy - h, w, h * 0.28);
    }
  }

  // a bobbing arrow over anything worth picking up
  function marker(x, y, sc) {
    var m = Math.max(4, sc * 0.3); y -= m + Math.sin(lastT / 160) * m * 0.35;
    ctx.fillStyle = '#FFC629'; ctx.strokeStyle = '#1B2A4A'; ctx.lineWidth = Math.max(1, sc * 0.05);
    ctx.beginPath(); ctx.moveTo(x - m, y - m); ctx.lineTo(x + m, y - m); ctx.lineTo(x, y + m * 0.4); ctx.closePath(); ctx.fill(); ctx.stroke();
  }

  function drawCrow(sx, sy, sc, stunned, noHat, dropping) {
    // The art: the Greedy Crow from behind, in the 200 x 260 character space. Three wing frames
    // at 90 ms (1-2-3-2), his hatless, hit and dropping poses.
    var cid = stunned ? 'cw-back-hit' : noHat ? 'cw-back-fly-nohat' : dropping ? 'cw-back-drop' : ['cw-back-fly-1', 'cw-back-fly-2', 'cw-back-fly-3', 'cw-back-fly-2'][Math.floor(lastT / 90) & 3];
    var cp = art ? art(cid) : null;
    if (cp) { var cw = 3.0 * sc, ch = cw * 1.3; ctx.drawImage(cp.c, sx - cw / 2, sy - 2.4 * sc - ch * 0.46, cw, ch); return; }
    var u = sc, flap = Math.sin(lastT / 90) * 0.35 * u;
    sy -= 2.4 * u;
    ctx.fillStyle = '#1B2A4A';
    ctx.beginPath(); ctx.moveTo(sx - 1.3 * u, sy - flap); ctx.lineTo(sx, sy - 0.25 * u); ctx.lineTo(sx + 1.3 * u, sy - flap); ctx.lineTo(sx + 0.5 * u, sy + 0.3 * u); ctx.lineTo(sx - 0.5 * u, sy + 0.3 * u); ctx.fill();
    ctx.fillStyle = '#2A3A5E'; ctx.beginPath(); ctx.ellipse(sx, sy + 0.2 * u, 0.5 * u, 0.62 * u, 0, 0, 7); ctx.fill();
    ctx.fillStyle = '#1B2A4A'; ctx.beginPath(); ctx.arc(sx, sy - 0.5 * u, 0.34 * u, 0, 7); ctx.fill();
    if (stunned) { ctx.fillStyle = '#FFFFFF'; ctx.beginPath(); ctx.arc(sx, sy - 0.45 * u, 0.3 * u, 0, 7); ctx.fill(); }
    else if (!noHat) { ctx.fillStyle = '#0F1A30'; ctx.fillRect(sx - 0.26 * u, sy - 1.3 * u, 0.52 * u, 0.5 * u); ctx.fillRect(sx - 0.45 * u, sy - 0.86 * u, 0.9 * u, 0.12 * u); }
  }

  function drawPlayer(px, py, shadowY, u) {
    u *= RIDER_SCALE;
    var rot = S.tilt + S.spin + (S.crash > 0 ? Math.sin(S.crash * 24) * 0.3 : 0);
    out.px = px; out.py = py; out.u = u; out.rot = rot; out.giant = S.mode === 'giant';
    ctx.fillStyle = 'rgba(27,42,74,.16)'; ctx.beginPath(); ctx.ellipse(px, shadowY, 0.85 * u, 0.22 * u, 0, 0, 7); ctx.fill();
    if (!S.air) {
      // the trail the sled ploughs: a darker channel in the snow with a groove under each runner
      // (Alan, 2 Oct). The camera is behind the sled, so the trail runs from it to the bottom edge.
      ctx.fillStyle = 'rgba(27,42,74,.09)'; ctx.beginPath(); ctx.moveTo(px - 0.72 * u, py); ctx.lineTo(px + 0.72 * u, py); ctx.lineTo(px + 0.95 * u, H); ctx.lineTo(px - 0.95 * u, H); ctx.fill();
      ctx.strokeStyle = 'rgba(27,42,74,.2)'; ctx.lineWidth = 0.13 * u; ctx.beginPath(); ctx.moveTo(px - 0.6 * u, py); ctx.lineTo(px - 0.8 * u, H); ctx.moveTo(px + 0.6 * u, py); ctx.lineTo(px + 0.8 * u, H); ctx.stroke();
    }
    if (S.mode === 'giant' && art && art('dd-player-snowball-1')) {
      var gp = art('dd-player-snowball-' + (1 + ((lastT / 160) & 1))), gw = 3.0 * u;
      ctx.drawImage(gp.c, px - gw / 2, py - gw, gw, gw);
      return;
    }
    // With the art loaded the sled, its rocket flame and the rider are one DOM stack over the
    // canvas (PigMountain.jsx <PmRider>), so nothing more is painted here.
    if (art && art('cw-back-fly-1') && S.mode !== 'giant') return;
    if (S.mode === 'giant') {
      var gr = 1.5 * u;
      ctx.fillStyle = '#FFFFFF'; ctx.strokeStyle = '#3D7CC9'; ctx.lineWidth = 0.12 * u;
      ctx.beginPath(); ctx.arc(px, py - gr, gr, 0, 7); ctx.fill(); ctx.stroke();
      ctx.save(); ctx.translate(px, py - gr); ctx.rotate(-lastT / 90); ctx.beginPath(); ctx.arc(0, 0, gr * 0.6, 0.3, 2.4); ctx.stroke(); ctx.beginPath(); ctx.arc(0, 0, gr * 0.3, 3.3, 5.6); ctx.stroke(); ctx.restore();
      return;
    }
    if (S.mode === 'rocket') {
      var fl = (0.9 + Math.sin(lastT / 40) * 0.3) * u;
      ctx.fillStyle = '#F08A3C'; ctx.beginPath(); ctx.moveTo(px - 0.5 * u, py); ctx.lineTo(px, py + fl * 1.4); ctx.lineTo(px + 0.5 * u, py); ctx.fill();
      ctx.fillStyle = '#FFC629'; ctx.beginPath(); ctx.moveTo(px - 0.25 * u, py); ctx.lineTo(px, py + fl * 0.8); ctx.lineTo(px + 0.25 * u, py); ctx.fill();
    }
    ctx.save(); ctx.translate(px, py - 0.5 * u);
    ctx.rotate(rot);
    ctx.fillStyle = '#D94B4B'; rr(-0.8 * u, 0.12 * u, 1.6 * u, 0.4 * u, 0.15 * u);
    ctx.fillStyle = '#B63A3A'; rr(-0.8 * u, 0.36 * u, 1.6 * u, 0.16 * u, 0.08 * u);
    ctx.restore();
  }
  function rr(x, y, w, h, r) { ctx.beginPath(); ctx.moveTo(x + r, y); ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r); ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r); ctx.fill(); }

  function draw() {
    backdrop();
    var camZ = S.z - CAM_DIST, base = Math.floor(camZ / PM.SEG), camY = groundY(S, S.z) * PM.VEX + CAM_H, camX = S.x * 0.7;
    var pct = camZ / PM.SEG - base, b0 = segAt(S, base);
    var x = 0, dx = b0 ? -b0.curve * pct : 0, minY = H, n, sg, nx;
    for (n = 0; n < PM.DRAW; n++) {
      sg = segAt(S, base + n); nx = segAt(S, base + n + 1);
      if (!sg || !nx) break;
      var z1 = (base + n) * PM.SEG - camZ, z2 = z1 + PM.SEG;
      var xo1 = x, xo2 = x + dx; x += dx; dx += sg.curve;
      sg.vis = false; sg.clip = minY;
      if (z1 < 1.5) continue;
      var s1 = F / z1, s2 = F / z2;
      var X1 = W / 2 + (xo1 - camX) * s1, X2 = W / 2 + (xo2 - camX) * s2;
      var Y1 = HY - (sg.y * PM.VEX - camY) * s1, Y2 = HY - (nx.y * PM.VEX - camY) * s2;
      sg.vis = true; sg.X = X1; sg.Y = Y1; sg.S = s1; sg.xo = xo1; sg.xo2 = xo2;
      if (Y2 >= minY) continue;
      // Snow is one tone with a texture, not alternating bands (Alan, 2 Oct): each 5 m of course
      // carries a few drift marks at fixed places, so they slide towards the sled and speed
      // still reads. The marks are placed from the segment's number, never from the clock.
      var sid = base + n;
      ctx.save(); ctx.beginPath(); ctx.rect(0, 0, W, minY); ctx.clip();
      var Zs = ZONES[sg.zone];
      ctx.fillStyle = sg.gap ? '#27425E' : Zs.sA; ctx.fillRect(0, Y2 - 0.5, W, Y1 - Y2 + 1);
      ctx.fillStyle = sg.gap ? '#1A2F47' : Zs.tA;
      ctx.beginPath(); ctx.moveTo(X1 - sg.half * s1, Y1); ctx.lineTo(X1 + sg.half * s1, Y1); ctx.lineTo(X2 + nx.half * s2, Y2 - 0.5); ctx.lineTo(X2 - nx.half * s2, Y2 - 0.5); ctx.fill();
      if (!sg.gap && Y1 - Y2 > 0.8) {
        var dh = Y1 - Y2, mi, hsh, mx, mw, my;
        for (mi = 0; mi < 5; mi++) {
          hsh = ((sid * 73856093) ^ ((mi + 1) * 19349663)) >>> 0;
          var onCourse = mi < 3, fx = ((hsh % 1000) / 1000) * 2 - 1;
          mx = onCourse ? X1 + fx * sg.half * 0.92 * s1 : X1 + (fx < 0 ? -1 : 1) * (sg.half + 1.2 + Math.abs(fx) * 9) * s1;
          mw = (0.5 + ((hsh >>> 10) % 100) / 100 * 0.9) * s1; my = Y1 - dh * (((hsh >>> 17) % 100) / 100);
          ctx.fillStyle = onCourse ? Zs.tB : Zs.sB;
          ctx.beginPath(); ctx.ellipse(mx, my, mw, Math.max(0.6, dh * 0.2), 0, 0, 7); ctx.fill();
          if (onCourse && (hsh & 3) === 0) { ctx.fillStyle = Zs.sB; ctx.beginPath(); ctx.ellipse(mx + mw * 0.2, my + dh * 0.05, mw * 0.45, Math.max(0.5, dh * 0.09), 0, 0, 7); ctx.fill(); }
        }
      }
      ctx.restore();
      minY = Y2;
    }
    var last = n - 1, pIdx = Math.floor(S.z / PM.SEG), cIdx = S.race ? Math.floor(S.race.crowZ / PM.SEG) : S.raid ? Math.floor(S.raid.z / PM.SEG) : -1;
    var crowX = S.race ? 2.0 : S.raid ? S.raid.x : 0, bIdx = S.boulder ? Math.floor(S.boulder.z / PM.SEG) : -1, mvs = S.movers, mq;
    var u = F / CAM_DIST, pSeg = segAt(S, pIdx), pf = S.z / PM.SEG - pIdx, px = W / 2 + (S.x - camX + (pSeg ? pSeg.xo + (pSeg.xo2 - pSeg.xo) * pf : 0)) * u;
    var shadowY = HY + CAM_H * u, py = shadowY - S.h * u, crowScr = null;
    // place a moving thing at its exact distance, not at the edge of its 5 m segment
    var at = function (z, g) {
      var dz = z - camZ; if (dz < 1.5) return null;
      var f2 = z / PM.SEG - Math.floor(z / PM.SEG), sc = F / dz;
      return { x: W / 2 + (g.xo + (g.xo2 - g.xo) * f2 - camX) * sc, y: HY - (groundY(S, z) * PM.VEX - camY) * sc, s: sc };
    };
    for (n = last; n >= 0; n--) {
      var idx = base + n; sg = segAt(S, idx);
      if (!sg) continue;
      if (idx === cIdx && sg.vis) {
        var cp = at(S.race ? S.race.crowZ : S.raid.z, sg);
        if (cp) {
          crowScr = { x: cp.x + crowX * cp.s, y: cp.y - 2.4 * cp.s };
          drawCrow(cp.x + crowX * cp.s, cp.y, cp.s, S.race ? S.race.stun > 0 : false, S.raid && S.raid.hit, !!S.raid && !S.raid.hit && S.raid.next < 0.35);
        }
      }
      if (idx === pIdx) drawPlayer(px, py, shadowY, u);
      if (!sg.vis) continue;
      ctx.save(); ctx.beginPath(); ctx.rect(0, 0, W, sg.clip); ctx.clip();
      curZone = sg.zone;
      for (var k = 0; k < sg.sprites.length; k++) { var o = sg.sprites[k]; drawSprite(o, sg.X + o.x * sg.S, sg.Y, sg.S); }
      for (mq = 0; mq < mvs.length; mq++) if (mvs[mq].on && !mvs[mq].hit && Math.floor(mvs[mq].z / PM.SEG) === idx) drawSprite({ t: mvs[mq].kind, w: 1, h: 1, c: mvs[mq].c, dir: mvs[mq].dir }, sg.X + mvs[mq].x * sg.S, sg.Y, sg.S);
      ctx.restore();
      if (idx === bIdx) { var bp = at(S.boulder.z, sg); if (bp) drawSprite({ t: 'boulder', w: 4, h: 4 }, bp.x + S.x * 0.6 * bp.s, bp.y, bp.s); }
    }
    if (ZONES[S.zone].fog) {
      var fg = ctx.createLinearGradient(0, 0, 0, H * 0.62);
      fg.addColorStop(0, 'rgba(240,245,248,1)'); fg.addColorStop(0.55, 'rgba(240,245,248,.9)'); fg.addColorStop(1, 'rgba(240,245,248,0)');
      ctx.fillStyle = fg; ctx.fillRect(0, 0, W, H * 0.62);
      ctx.strokeStyle = 'rgba(255,255,255,.9)'; ctx.lineWidth = 2; ctx.beginPath();
      for (var sn = 0; sn < 40; sn++) { var fx = (sn * 97 + lastT * 0.12) % W, fy = (sn * 53 + lastT * 0.5) % H; ctx.moveTo(fx, fy); ctx.lineTo(fx - 4, fy + 9); }
      ctx.stroke();
    }
    if (S.shot) {
      var t = S.shot.t / 0.35, tx = crowScr ? crowScr.x : W * 0.75, ty = crowScr ? crowScr.y : H * 0.3;
      ctx.fillStyle = '#FFFFFF'; ctx.strokeStyle = '#9DBBD0'; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.arc(px + (tx - px) * t, py - u + (ty - py + u) * t - Math.sin(t * Math.PI) * 40, 7, 0, 7); ctx.fill(); ctx.stroke();
    }
    if (S.boostFx > 0) {
      ctx.strokeStyle = 'rgba(255,255,255,.85)'; ctx.lineWidth = 3; ctx.beginPath();
      for (var q = 0; q < 8; q++) { var ang = q / 8 * Math.PI * 2 + 0.3, r1 = W * 0.42, r2 = W * 0.62; ctx.moveTo(W / 2 + Math.cos(ang) * r1, H * 0.5 + Math.sin(ang) * r1 * 1.5); ctx.lineTo(W / 2 + Math.cos(ang) * r2, H * 0.5 + Math.sin(ang) * r2 * 1.5); }
      ctx.stroke();
    }
    out.crow = crowScr;
  }

  draw();
  return out;
}

// How many metres one CSS pixel of finger travel is worth at the sled's distance.
export function metresPerPixel(view) {
  return 1.35 / ((view.H * 0.95) / CAM_DIST);
}
